import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Calculator, 
  Dog,
  Activity,
  Scale,
  Calendar,
  Info
} from 'lucide-react';
import api from '../services/api';

function FeedingCalculator() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    weight: 25,
    age_months: 24,
    activity_level: 'moderate',
    is_neutered: true
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateCalories = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/nutrition/calorie-calculator', formData);
      setResult(response.data);
    } catch (error) {
      console.error('Error calculating calories:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAgeCategory = (months) => {
    if (months < 4) return 'Young Puppy';
    if (months < 12) return 'Puppy';
    if (months < 84) return 'Adult';
    return 'Senior';
  };

  const getActivityDescription = (level) => {
    const descriptions = {
      low: 'Mostly indoor, minimal exercise, older or less active dogs',
      moderate: 'Regular walks, normal play, average activity level',
      high: 'Active dogs, regular running or hiking, working dogs',
      very_high: 'Very active working dogs, sport dogs, or extreme activity'
    };
    return descriptions[level] || '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/app/nutrition')}
          className="flex items-center space-x-2 text-purple-600 hover:text-purple-800 mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back to Nutrition</span>
        </button>

        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2 flex items-center justify-center">
            <Calculator className="w-10 h-10 mr-3 text-purple-600" />
            Daily Calorie Calculator
          </h1>
          <p className="text-xl text-gray-600">
            Calculate your dog's daily caloric needs based on their profile
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-semibold mb-6 flex items-center">
              <Dog className="w-6 h-6 mr-2 text-purple-600" />
              Dog Information
            </h2>
            
            <form onSubmit={calculateCalories} className="space-y-6">
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Scale className="w-4 h-4 mr-2 text-gray-500" />
                  Weight (pounds)
                </label>
                <input
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  min="1"
                  max="300"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Enter your dog's current weight</p>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 mr-2 text-gray-500" />
                  Age (months)
                </label>
                <input
                  type="number"
                  value={formData.age_months}
                  onChange={(e) => setFormData({ ...formData, age_months: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  min="1"
                  max="240"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Life stage: {getAgeCategory(formData.age_months)}
                </p>
              </div>

              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Activity className="w-4 h-4 mr-2 text-gray-500" />
                  Activity Level
                </label>
                <select
                  value={formData.activity_level}
                  onChange={(e) => setFormData({ ...formData, activity_level: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="low">Low Activity</option>
                  <option value="moderate">Moderate Activity</option>
                  <option value="high">High Activity</option>
                  <option value="very_high">Very High Activity</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {getActivityDescription(formData.activity_level)}
                </p>
              </div>

              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.is_neutered}
                    onChange={(e) => setFormData({ ...formData, is_neutered: e.target.checked })}
                    className="rounded text-purple-600 focus:ring-purple-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Spayed/Neutered
                  </span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Neutered dogs typically need fewer calories
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Calculating...' : 'Calculate Daily Calories'}
              </button>
            </form>
          </div>

          <div className="space-y-6">
            {result && (
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h2 className="text-2xl font-semibold mb-6">Calorie Requirements</h2>
                
                <div className="text-center mb-6">
                  <div className="text-5xl font-bold text-purple-600 mb-2">
                    {result.daily_calories}
                  </div>
                  <div className="text-lg text-gray-600">calories per day</div>
                </div>

                <div className="space-y-4 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Resting Energy (RER)</span>
                    <span className="font-semibold">{result.rer} calories</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Activity Multiplier</span>
                    <span className="font-semibold">{result.multiplier.toFixed(1)}x</span>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start space-x-2">
                    <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-blue-800">
                      {result.notes}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Understanding the Results</h3>
              <div className="space-y-3 text-sm text-gray-700">
                <p>
                  <strong>RER (Resting Energy Requirement):</strong> The baseline calories your dog needs at rest.
                </p>
                <p>
                  <strong>Activity Multiplier:</strong> Adjusts RER based on age, activity level, and whether your dog is neutered.
                </p>
                <p>
                  <strong>Daily Calories:</strong> The estimated total calories your dog needs per day to maintain their current weight.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start space-x-2">
                <Info className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Important Notes:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>This is an estimate based on average requirements</li>
                    <li>Individual dogs may need 20-30% more or less</li>
                    <li>Monitor your dog's weight and body condition</li>
                    <li>Consult your veterinarian for specific advice</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FeedingCalculator;